import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Collapse,
  Alert,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * Meeting Confirmation Tab Component
 * Handles configuration for automated meeting confirmations via SMS or voice
 */
const MeetingConfirmationTab = ({ 
  customerId, 
  calendars, 
  currentCalendarIndex,
  onConfigChange,
  confirmationConfig,
  setConfirmationConfig,
  isGlobalView = false,
  disabled = false
}) => {
  const { t } = useTranslation();
  
  const [expandedSections, setExpandedSections] = useState({
    smsTemplate: true,
    voiceTemplate: false,
    perCalendar: false,
  });
  
  const [selectedLanguage, setSelectedLanguage] = useState('pl');
  const [perCalendarOverride, setPerCalendarOverride] = useState({
    enabled: false,
    calendarName: '',
    config: {}
  });

  // Initialize default config if not present
  useEffect(() => {
    if (!confirmationConfig) {
      setConfirmationConfig({
        enabled: false,
        method: 'sms',
        hours_before_event: 24,
        max_retry_attempts: 3,
        retry_interval_hours: 4,
        auto_cancel_on_timeout: false,
        language: 'pl',
        templates: {
          sms: {
            pl: 'Dzień dobry! Prosimy o potwierdzenie wizyty w dniu {date} o godzinie {time}. Odpowiedz TAK lub NIE.',
            en: 'Hello! Please confirm your appointment on {date} at {time}. Reply YES or NO.'
          },
          voice: {
            pl: 'Dzień dobry. Prosimy o potwierdzenie wizyty w {business_name} w dniu {date} o godzinie {time}. Naciśnij 1 aby potwierdzić, 2 aby odwołać.',
            en: 'Hello. Please confirm your appointment at {business_name} on {date} at {time}. Press 1 to confirm, 2 to cancel.'
          }
        }
      });
    }
  }, [confirmationConfig, setConfirmationConfig]);

  const handleToggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleConfigChange = (field, value) => {
    const newConfig = {
      ...confirmationConfig,
      [field]: value
    };
    setConfirmationConfig(newConfig);
    // NOTE: Only update local state - don't trigger database save
    // Database save happens when user clicks "Zapisz zmiany" button
  };

  const handleTemplateChange = (type, language, value) => {
    const newConfig = {
      ...confirmationConfig,
      templates: {
        ...confirmationConfig.templates,
        [type]: {
          ...confirmationConfig.templates?.[type],
          [language]: value
        }
      }
    };
    setConfirmationConfig(newConfig);
    // NOTE: Only update local state - don't trigger database save
    // Database save happens when user clicks "Zapisz zmiany" button
  };

  const getCharacterCount = (text) => {
    return text ? text.length : 0;
  };

  const validateHours = (value) => {
    const num = parseInt(value);
    return num >= 1 && num <= 168;
  };

  const validateAttempts = (value) => {
    const num = parseInt(value);
    return num >= 0 && num <= 10;
  };

  const validateInterval = (value) => {
    const num = parseInt(value);
    return num >= 1 && num <= 24;
  };

  if (!confirmationConfig) {
    return <Typography>Loading configuration...</Typography>;
  }

  return (
    <Box sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      {/* Global/Calendar View Header */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: isGlobalView ? 'primary.50' : 'info.50' }}>
        <Typography variant="h6" gutterBottom>
          {isGlobalView 
            ? t('calendarConfig.confirmation.globalSettings')
            : t('calendarConfig.confirmation.calendarSettings')
          }
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isGlobalView
            ? t('calendarConfig.confirmation.globalSettingsDescription')
            : t('calendarConfig.confirmation.calendarSettingsDescription')
          }
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Enable Confirmations */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={confirmationConfig.enabled || false}
                onChange={(e) => handleConfigChange('enabled', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  {t('calendarConfig.confirmation.enabled')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('calendarConfig.confirmation.description')}
                </Typography>
              </Box>
            }
          />
        </Grid>

        {/* Method Selection */}
        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset" disabled={!confirmationConfig.enabled}>
            <FormLabel component="legend">
              {t('calendarConfig.confirmation.method')}
            </FormLabel>
            <RadioGroup
              value={confirmationConfig.method || 'sms'}
              onChange={(e) => handleConfigChange('method', e.target.value)}
            >
              <FormControlLabel
                value="sms"
                control={<Radio />}
                label={t('calendarConfig.confirmation.methodSms')}
              />
              <FormControlLabel
                value="voice"
                control={<Radio />}
                label={t('calendarConfig.confirmation.methodVoice')}
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Language Selection */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            disabled={!confirmationConfig.enabled}
            label={t('calendarConfig.confirmation.language')}
            value={confirmationConfig.language || 'pl'}
            onChange={(e) => handleConfigChange('language', e.target.value)}
          >
            <MenuItem value="pl">{t('calendarConfig.confirmation.templates.polish')}</MenuItem>
            <MenuItem value="en">{t('calendarConfig.confirmation.templates.english')}</MenuItem>
          </TextField>
        </Grid>

        {/* Hours Before Event */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            disabled={!confirmationConfig.enabled}
            label={t('calendarConfig.confirmation.hoursBefore')}
            value={confirmationConfig.hours_before_event || 24}
            onChange={(e) => handleConfigChange('hours_before_event', parseInt(e.target.value))}
            helperText={t('calendarConfig.confirmation.hoursBeforeHelp')}
            error={!validateHours(confirmationConfig.hours_before_event)}
            inputProps={{ min: 1, max: 168 }}
          />
        </Grid>

        {/* Max Retry Attempts */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            disabled={!confirmationConfig.enabled}
            label={t('calendarConfig.confirmation.maxAttempts')}
            value={confirmationConfig.max_retry_attempts || 3}
            onChange={(e) => handleConfigChange('max_retry_attempts', parseInt(e.target.value))}
            helperText={t('calendarConfig.confirmation.maxAttemptsHelp')}
            error={!validateAttempts(confirmationConfig.max_retry_attempts)}
            inputProps={{ min: 0, max: 10 }}
          />
        </Grid>

        {/* Retry Interval */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            disabled={!confirmationConfig.enabled}
            label={t('calendarConfig.confirmation.retryInterval')}
            value={confirmationConfig.retry_interval_hours || 4}
            onChange={(e) => handleConfigChange('retry_interval_hours', parseInt(e.target.value))}
            helperText={t('calendarConfig.confirmation.retryIntervalHelp')}
            error={!validateInterval(confirmationConfig.retry_interval_hours)}
            inputProps={{ min: 1, max: 24 }}
          />
        </Grid>

        {/* Auto Cancel */}
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                disabled={!confirmationConfig.enabled}
                checked={confirmationConfig.auto_cancel_on_timeout || false}
                onChange={(e) => handleConfigChange('auto_cancel_on_timeout', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  {t('calendarConfig.confirmation.autoCancel')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('calendarConfig.confirmation.autoCancelHelp')}
                </Typography>
              </Box>
            }
          />
        </Grid>

        {/* Validation Errors */}
        {confirmationConfig.enabled && (
          <Grid item xs={12}>
            {!validateHours(confirmationConfig.hours_before_event) && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {t('calendarConfig.confirmation.validation.hoursRange')}
              </Alert>
            )}
            {!validateAttempts(confirmationConfig.max_retry_attempts) && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {t('calendarConfig.confirmation.validation.attemptsRange')}
              </Alert>
            )}
            {!validateInterval(confirmationConfig.retry_interval_hours) && (
              <Alert severity="error">
                {t('calendarConfig.confirmation.validation.intervalRange')}
              </Alert>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* SMS Template Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => handleToggleSection('smsTemplate')}
            >
              <Typography variant="h6">
                {t('calendarConfig.confirmation.templates.sms')}
              </Typography>
              {expandedSections.smsTemplate ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedSections.smsTemplate}>
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('calendarConfig.confirmation.templates.placeholders')}
                  <br />
                  {t('calendarConfig.confirmation.templates.smsHelp')}
                </Alert>
                
                {/* Language Tab Selection */}
                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: selectedLanguage === 'pl' ? 'primary.main' : 'grey.200',
                      color: selectedLanguage === 'pl' ? 'white' : 'text.primary'
                    }}
                    onClick={() => setSelectedLanguage('pl')}
                  >
                    PL
                  </Box>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: selectedLanguage === 'en' ? 'primary.main' : 'grey.200',
                      color: selectedLanguage === 'en' ? 'white' : 'text.primary'
                    }}
                    onClick={() => setSelectedLanguage('en')}
                  >
                    EN
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  disabled={!confirmationConfig.enabled}
                  value={confirmationConfig.templates?.sms?.[selectedLanguage] || ''}
                  onChange={(e) => handleTemplateChange('sms', selectedLanguage, e.target.value)}
                  helperText={`${t('calendarConfig.confirmation.templates.characterCount')} ${getCharacterCount(confirmationConfig.templates?.sms?.[selectedLanguage])} / 160`}
                />
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        {/* Voice Template Section */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => handleToggleSection('voiceTemplate')}
            >
              <Typography variant="h6">
                {t('calendarConfig.confirmation.templates.voice')}
              </Typography>
              {expandedSections.voiceTemplate ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={expandedSections.voiceTemplate}>
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('calendarConfig.confirmation.templates.placeholders')}
                  <br />
                  {t('calendarConfig.confirmation.templates.voiceHelp')}
                </Alert>

                {/* Language Tab Selection */}
                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: selectedLanguage === 'pl' ? 'primary.main' : 'grey.200',
                      color: selectedLanguage === 'pl' ? 'white' : 'text.primary'
                    }}
                    onClick={() => setSelectedLanguage('pl')}
                  >
                    PL
                  </Box>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: selectedLanguage === 'en' ? 'primary.main' : 'grey.200',
                      color: selectedLanguage === 'en' ? 'white' : 'text.primary'
                    }}
                    onClick={() => setSelectedLanguage('en')}
                  >
                    EN
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  disabled={!confirmationConfig.enabled}
                  value={confirmationConfig.templates?.voice?.[selectedLanguage] || ''}
                  onChange={(e) => handleTemplateChange('voice', selectedLanguage, e.target.value)}
                  helperText={`${t('calendarConfig.confirmation.templates.characterCount')} ${getCharacterCount(confirmationConfig.templates?.voice?.[selectedLanguage])}`}
                />
              </Box>
            </Collapse>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MeetingConfirmationTab;
